// Single source of truth for per-page SEO metadata.
// Consumed BOTH at build time (scripts/materialize-routes.js stamps these into
// each route's served HTML so crawlers get real per-page title/description/
// canonical without running JS) AND client-side (usePageMeta, for SPA nav).
// CommonJS so the Node build script can require it and React can import it.
//
// North Shore, Auckland is the primary service area — titles/descriptions lead
// with locksmith intent + North Shore / Auckland geography. Phone woven into
// descriptions for click-to-call in the SERP snippet.

const { serviceAreas } = require('../content/serviceAreas');
const { posts } = require('../content/blog');

const SITE_ORIGIN = 'https://www.shoredoors.co.nz';
const PHONE = '022 513 3957';

const routeMeta = {
  '/': {
    title: 'Locksmith North Shore Auckland | Shore Doors Locksmiths',
    description:
      `Mobile locksmiths for Auckland's North Shore — lockouts, rekeying, key cutting, lock repairs and commercial door hardware. Fixed quotes, 12-month guarantee. Call ${PHONE}.`,
  },
  '/emergency-locksmith': {
    title: 'Emergency Locksmith North Shore Auckland | Shore Doors',
    description:
      `Locked out on the North Shore? Fast mobile emergency locksmith for homes and businesses — damage-free entry, fixed quote before we start. Call ${PHONE}.`,
  },
  '/residential': {
    title: 'Residential Locksmith North Shore | Shore Doors Locksmiths',
    description:
      `North Shore residential locksmiths — lockouts, rekeying, lock repairs, key cutting and home security upgrades. Mobile service, we come to you. Call ${PHONE}.`,
  },
  '/residential/lock-rekeying': {
    title: 'Lock Rekeying North Shore Auckland | Shore Doors',
    description:
      `Rekey your locks on the North Shore after moving house, lost keys or a tenancy change — old keys stop working. From $60 per lock, mobile service. Call ${PHONE}.`,
  },
  '/residential/lock-repair-replacement': {
    title: 'Lock Repair & Replacement North Shore | Shore Doors',
    description:
      `Sticking, worn or broken locks repaired or replaced across Auckland's North Shore by commercial-grade installers. 12-month workmanship guarantee. Call ${PHONE}.`,
  },
  '/residential/key-cutting': {
    title: 'Mobile Key Cutting North Shore Auckland | Shore Doors',
    description:
      `Keys cut at your place on the North Shore from our mobile van — house, window and padlock keys, keyed-alike options. Call ${PHONE}.`,
  },
  '/residential/home-security-upgrades': {
    title: 'Home Security Upgrades North Shore | Shore Doors',
    description:
      `Deadlocks, window locks, sliding-door locks, smart locks and safes fitted across the North Shore. Insurance-friendly home security done properly. Call ${PHONE}.`,
  },
  '/commercial': {
    title: 'Commercial Locksmith Auckland | Shore Doors Locksmiths',
    description:
      `Commercial locksmiths and door hardware across Auckland — the install team security companies trust. Master keying, access control, maintenance. Request a quote.`,
  },
  '/commercial/commercial-locksmith': {
    title: 'Commercial Locksmith North Shore & Auckland | Shore Doors',
    description:
      `Business lockouts, rekeying after staff changes, restricted keys and shopfront hardware across Auckland — from the installers the pros use. Request a quote.`,
  },
  '/commercial/door-hardware-installation': {
    title: 'Commercial Door Hardware Installation Auckland | Shore Doors',
    description:
      `Mortice locks, panic hardware, door closers and fire-door hardware installed to NZ Building Code across Auckland. Trusted commercial installers. Request a quote.`,
  },
  '/commercial/access-control-installation': {
    title: 'Access Control Installation Auckland | Shore Doors',
    description:
      `Maglocks, electric strikes, drop bolts and wireless locks installed to your integrator's spec across Auckland — cabling, commissioning, handover. Request a quote.`,
  },
  '/commercial/door-automation': {
    title: 'Automatic Door Openers Auckland — AHS A200 | Shore Doors',
    description:
      `Auckland's only certified AHS A200 door actuator installer — automatic door opening, installation, repair and servicing. Accessibility and ACC work for disabled customers. Request a quote.`,
  },
  '/commercial/master-key-systems': {
    title: 'Master Key Systems Auckland | Shore Doors Locksmiths',
    description:
      `Master key and restricted key systems designed, installed and managed for Auckland businesses, schools and body corporates — proper key control. Request a quote.`,
  },
  '/commercial/maintenance-iqp': {
    title: 'IQP Door Inspections & BWOF Maintenance Auckland | Shore Doors',
    description:
      `IQP inspections for automatic doors, fire doors and exit hardware, Building Warrant of Fitness (BWOF) support and scheduled door maintenance for Auckland facilities teams. Request a quote.`,
  },
  '/partners': {
    title: 'Installation Partner for Security Companies | Shore Doors',
    description:
      `Shore Doors is the installation delivery partner Auckland's access control companies and builders rely on — hardware installs, cabling, commissioning, handover.`,
  },
  '/industries': {
    title: 'Industries We Serve | Shore Doors Locksmiths Auckland',
    description:
      `Door hardware and access control installation for healthcare, education, government, data centres and corporate offices across Auckland. Request a quote.`,
  },
  '/projects': {
    title: 'Completed Projects | Shore Doors Locksmiths Auckland',
    description:
      `Real installations across Auckland — North Shore Hospital, BNZ Queen Street, Fujifilm House of Photography and more. Door hardware and access control.`,
  },
  '/reviews': {
    title: 'Reviews & Testimonials | Shore Doors Locksmiths',
    description:
      `What Auckland's security companies and clients say about Shore Doors Locksmiths — reliable installs, quality workmanship and easy to deal with.`,
  },
  '/about': {
    title: 'About | North Shore Locksmiths | Shore Doors Locksmiths',
    description:
      `From trusted install partner of Auckland's security companies to full-service North Shore locksmiths — our story, team and standards. Call ${PHONE}.`,
  },
  '/contact': {
    title: 'Contact | Locksmith North Shore Auckland | Shore Doors',
    description:
      `Call Shore Doors Locksmiths on ${PHONE} or send an enquiry — North Shore residential and Auckland commercial locksmith and door hardware services.`,
  },
  '/pricing': {
    title: 'Locksmith Prices North Shore Auckland | Shore Doors Locksmiths',
    description:
      `Clear locksmith pricing for the North Shore — $120 call-out including the first 15 minutes on site, then $30 per 15 minutes. Rekeying from $60 per lock. Fixed quote before any work starts. Call ${PHONE}.`,
  },
  '/service-areas': {
    title: 'Locksmith Service Areas — North Shore Auckland | Shore Doors',
    description:
      `Shore Doors Locksmiths covers Auckland's North Shore — Takapuna, Devonport, Albany, Glenfield, Browns Bay, Milford and more. Mobile locksmith, we come to you.`,
  },
  '/blog': {
    title: 'Locksmith Tips & Guides | Shore Doors Locksmiths',
    description:
      `Locksmith advice for Auckland's North Shore — what a locksmith costs, what to do when locked out, rekey vs replace, and more from Shore Doors Locksmiths.`,
  },
};

// Generate a metadata entry per suburb service-area page (single source of
// truth — the build script and client hook both read these).
for (const area of serviceAreas) {
  routeMeta[`/service-areas/${area.slug}`] = {
    title: `Locksmith ${area.name} | Shore Doors Locksmiths`,
    description:
      `Mobile locksmith in ${area.name}, North Shore — lockouts, rekeying, key cutting, lock repairs and home security. Fast local service, fixed quotes. Call ${PHONE}.`,
  };
}

// Blog posts — per-post meta from the post's own metaTitle/metaDescription.
for (const post of posts) {
  routeMeta[`/blog/${post.slug}`] = {
    title: post.metaTitle || `${post.title} | Shore Doors Locksmiths`,
    description: post.metaDescription || post.excerpt,
  };
}

module.exports = { routeMeta, SITE_ORIGIN, PHONE };
